import { useEffect, useState } from "react";
import { ApiError, fetchDailyDigest, type DailyDigestDto } from "../api";
import { AsOfBadge } from "../components/AsOfBadge";
import { PageHelper } from "../components/PageHelper";

function pctFmt(v: number | null | undefined, digits = 2): string {
  if (v === null || v === undefined) return "—";
  return `${(v * 100).toFixed(digits)}%`;
}

/** Severity chip. DEGRADED_KNOWN gets its own muted-amber treatment,
 * deliberately distinct from a plain "warn" chip -- Rule 6: a daily
 * known-baseline degradation (index paused on calendar runway) must never
 * look identical to a genuinely new fault. */
function StatusChip({ status }: { status: string }) {
  const map: Record<string, string> = {
    FRESH: "xs-chip-ok", HEALTHY: "xs-chip-ok",
    DEGRADED_KNOWN: "xs-chip-mute", PENDING: "xs-chip-mute",
    DEGRADED: "xs-chip-warn", PAUSED: "xs-chip-warn", UNKNOWN: "xs-chip-warn",
    STALE: "xs-chip-bad", FAILED: "xs-chip-bad",
  };
  const cls = map[status] || "xs-chip-mute";
  return <span className={`xs-chip ${cls}`}>{status}</span>;
}

function SystemHealthPanel({ h }: { h: DailyDigestDto["system_health"] }) {
  const known = h.top_status === "DEGRADED_KNOWN";
  return (
    <div className="card xs-card">
      <h3 className="xs-subhead">1. System health</h3>
      <AsOfBadge date={h.run_date} label="pipeline" />
      <div className="xs-risk-top" style={{ marginTop: 10 }}>
        <StatusChip status={h.top_status} />
        <span className="xs-reason" style={{ margin: 0 }}>{h.top_label}</span>
      </div>
      {known && (
        <div className="xs-honest-banner" style={{ marginTop: 8 }}>
          This reads amber every day the calendar runway is short -- that is the KNOWN,
          already-diagnosed baseline, not a fresh problem. A genuinely new fault (stock
          predictions breaking, or an index pause with sufficient runway) would surface
          here as a plain, un-muted status instead.
        </div>
      )}

      <table className="xs-table" style={{ marginTop: 12 }}>
        <thead><tr><th>Subsystem</th><th>Status</th><th>Reason</th></tr></thead>
        <tbody>
          <tr>
            <td>Index predictions</td>
            <td><StatusChip status={h.subsystems.index.status} /></td>
            <td className="xs-reason-cell">{h.subsystems.index.reason}</td>
          </tr>
          <tr>
            <td>Stock predictions / fires</td>
            <td><StatusChip status={h.subsystems.stock.status} /></td>
            <td className="xs-reason-cell">{h.subsystems.stock.reason}</td>
          </tr>
        </tbody>
      </table>

      <div className="xs-dl" style={{ marginTop: 10 }}>
        <span className="page-sub">Scorecard gated dates: <b>{h.gated_fire_date_count}</b></span>
      </div>
    </div>
  );
}

function RiskPanel({ r }: { r: DailyDigestDto["risk"] }) {
  if (r.status === "not_started") {
    return (
      <div className="card xs-card">
        <h3 className="xs-subhead">2. Risk</h3>
        <div className="xs-empty-state">{r.reason}</div>
      </div>
    );
  }
  return (
    <div className="card xs-card">
      <h3 className="xs-subhead">2. Risk</h3>
      <AsOfBadge date={r.as_of ?? null} label="risk" />
      <div className="xs-risk-top" style={{ marginTop: 10 }}>
        <span className={`xs-state-badge ${r.state === "tripped" ? "xs-state-tripped" : "xs-state-armed"}`}>
          {r.state?.toUpperCase()}
        </span>
        <span className="xs-reason" style={{ margin: 0 }}>
          drawdown {pctFmt(r.drawdown_pct)} &middot; exposure {((r.exposure_multiplier ?? 0) * 100).toFixed(0)}%
          {r.cumulative_return_pct != null && <> &middot; cumulative {pctFmt(r.cumulative_return_pct)}</>}
          {r.vs_benchmark_pct != null && <> &middot; vs. NIFTY {pctFmt(r.vs_benchmark_pct)}</>}
        </span>
      </div>
      {r.changed_since_yesterday ? (
        <div className="xs-honest-banner">Changed since yesterday: {r.change_note}</div>
      ) : (
        <div className="page-sub" style={{ marginTop: 6 }}>No change since yesterday. {r.change_note ?? ""}</div>
      )}
      {r.early_reading_caveat && (
        <div className="xs-empty-state" style={{ marginTop: 8 }}>
          Early reading: only {r.days_tracked} day(s) tracked (floor for a settled reading is{" "}
          {r.early_reading_floor_days}). Interpret the drawdown/state cautiously until more
          history accumulates.
        </div>
      )}
      {r.equity_honesty_note && (
        <div className="xs-reason-cell" style={{ maxWidth: "none", marginTop: 8 }}>{r.equity_honesty_note}</div>
      )}
    </div>
  );
}

const AGE_BUCKET_ORDER = ["0-2", "3-5", "6-10", "11-20", "21+"];

function BookAndFiresPanel({ b }: { b: DailyDigestDto["book_and_fires"] }) {
  return (
    <div className="card xs-card">
      <h3 className="xs-subhead">3. Book &amp; fire deltas</h3>

      <div style={{ marginBottom: 14 }}>
        <AsOfBadge date={b.book.book_as_of_date} label="book (weekly reconstruction)" />
        <div className="page-sub" style={{ marginTop: 4 }}>
          {b.book.status === "no_reconstruction_yet"
            ? "No reconstruction has run yet."
            : b.book.new_reconstruction_today
              ? `Book reconstructed TODAY -- ${b.book.n_positions} position(s).`
              : `Book held as of last reconstruction, ${b.book.book_age_trading_days} trading day(s) ago -- ${b.book.n_positions} position(s).`}
        </div>
        {b.book.book_date_mismatch && b.book.book_date_mismatch_note && (
          <div className="xs-honest-banner" style={{ marginTop: 6 }}>{b.book.book_date_mismatch_note}</div>
        )}
      </div>

      <div style={{ marginBottom: 14 }}>
        <div className="page-sub" style={{ fontWeight: 600 }}>
          Newly-firing stocks today -- {b.signal_fires_today.new_stock_count}
          <span style={{ fontWeight: 400, color: "var(--ink-soft)" }}>
            {" "}(of {b.signal_fires_today.total_distinct_stocks_firing_today} distinct stocks firing today total)
          </span>
        </div>
        <div className="xs-reason-cell" style={{ maxWidth: "none", margin: "4px 0" }}>
          {b.signal_fires_today.cadence_note} {b.signal_fires_today.note}
        </div>
        {b.signal_fires_today.new_stocks.length > 0 && (
          <table className="xs-table">
            <thead><tr><th>Ticker</th></tr></thead>
            <tbody>
              {b.signal_fires_today.new_stocks.map((f) => (
                <tr key={f.security_id}>
                  <td title={f.security_id}>{f.display_ticker}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div style={{ marginBottom: 14 }}>
        <div className="page-sub" style={{ fontWeight: 600 }}>
          Exits since last reconstruction
          {b.exits_since_last_reconstruction.count !== null ? ` -- ${b.exits_since_last_reconstruction.count}` : ""}
        </div>
        <div className="xs-reason-cell" style={{ maxWidth: "none", margin: "4px 0" }}>
          {b.exits_since_last_reconstruction.note}
        </div>
        {b.exits_since_last_reconstruction.tickers.length > 0 && (
          <div className="page-sub">{b.exits_since_last_reconstruction.tickers.join(", ")}</div>
        )}
      </div>

      <div>
        <div className="page-sub" style={{ fontWeight: 600, marginBottom: 6 }}>Held positions by signal age (trading days)</div>
        <div className="xs-sector-bars">
          {AGE_BUCKET_ORDER.map((bucket) => {
            const n = b.positions_by_signal_age[bucket] ?? 0;
            const max = Math.max(1, ...Object.values(b.positions_by_signal_age));
            return (
              <div className="xs-sector-row" key={bucket}>
                <span>{bucket}</span>
                <div className="xs-gauge-track" style={{ height: 10 }}>
                  <div className="xs-gauge-zone xs-gauge-zone-full" style={{ left: 0, width: `${(n / max) * 100}%` }} />
                </div>
                <span className="xs-num">{n}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export function DailyDigestPage({ token, onUnauthorized }: { token: string; onUnauthorized: () => void }) {
  const [digest, setDigest] = useState<DailyDigestDto | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchDailyDigest(token)
      .then(setDigest)
      .catch((err) => {
        if (err instanceof ApiError && err.status === 401) return onUnauthorized();
        setError(err instanceof ApiError ? err.message : "request failed");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  return (
    <div className="shell" style={{ maxWidth: 1040 }}>
      <div className="page-head">
        <h1>Daily Digest</h1>
        <div className="page-sub">
          One-glance system state: what else already computed, surfaced here. No new numbers.
        </div>
      </div>
      <PageHelper>
        A pure surfacing layer over Sizing/Portfolio/Risk/Signal-Half-Life/the daily pipeline --
        nothing on this page is computed live. It is snapshotted once a day, after the evening
        pipeline and the 19:45 risk reading, and never hides a broken subsystem behind a clean-
        looking book: System health always renders first.
      </PageHelper>

      {error && <div className="card xs-card"><div className="xs-error">{error}</div></div>}
      {!digest && !error && <div className="card xs-card"><div className="page-sub">Loading…</div></div>}

      {digest && (
        <>
          <div className="page-sub" style={{ marginBottom: 10 }}>
            Snapshot computed {digest.snapshot_computed_at} for trading day {digest.snapshot_run_date}.
          </div>
          <SystemHealthPanel h={digest.system_health} />
          <RiskPanel r={digest.risk} />
          <BookAndFiresPanel b={digest.book_and_fires} />
          <div className="card xs-card">
            <h3 className="xs-subhead">Signal Half-Life</h3>
            <div className="page-sub">{digest.half_life_status}</div>
          </div>
        </>
      )}
    </div>
  );
}
